import { create } from 'zustand'
import {
    DEFAULT_BENCHMARKING_REPORT_SETTINGS,
    DEFAULT_USAGE_AND_COST_DATA_SETS,
} from '../../../../utils/constants'
import {
    BenchmarkingReportSettings,
    BenchmarkingDisplay,
    UsageCostDataSet,
} from '@/hooks'
import { ViewType } from '@/stores/useViewAsToggle/useViewAsToggle'

export type AnalyticsStoreState = {
    usageAndCost: {
        hasDefaultDataSets: boolean
        isChartSummaryHovered: boolean
        isReportSettingsOpen: boolean
        dataSets: Array<UsageCostDataSet>
        setActiveDataSets: (dataSets: Array<UsageCostDataSet>) => void
        resetActiveDataSets: () => void
        setIsChartSummaryHovered: (value: boolean) => void
        setIsReportSettingsOpen: (value: boolean) => void
    }
    benchmarking: {
        reportSettings: BenchmarkingReportSettings
        setReportSettings: (
            settings: Partial<BenchmarkingReportSettings>
        ) => void
        hasDefaultSettings: boolean
        resetReportSettings: () => void
        hoveredDisplaySetting: BenchmarkingDisplay | null
        setHoveredDisplaySetting: (entity: BenchmarkingDisplay | null) => void
        selectedView: ViewType
        setSelectedView: (view: ViewType) => void
    }
}

const checkDefaultSettings = (settings: BenchmarkingReportSettings) =>
    JSON.stringify(settings) ===
    JSON.stringify(DEFAULT_BENCHMARKING_REPORT_SETTINGS)

const useAnalyticsStore = create<AnalyticsStoreState>((set) => ({
    usageAndCost: {
        hasDefaultDataSets: true,
        isChartSummaryHovered: false,
        isReportSettingsOpen: false,
        dataSets: DEFAULT_USAGE_AND_COST_DATA_SETS,
        setActiveDataSets: (dataSets) => {
            set((state) => {
                const hasDefaultDataSets =
                    JSON.stringify(dataSets) ===
                    JSON.stringify(DEFAULT_USAGE_AND_COST_DATA_SETS)
                return {
                    usageAndCost: {
                        ...state.usageAndCost,
                        dataSets,
                        hasDefaultDataSets,
                    },
                }
            })
        },
        resetActiveDataSets: () => {
            set((state) => ({
                usageAndCost: {
                    ...state.usageAndCost,
                    dataSets: DEFAULT_USAGE_AND_COST_DATA_SETS,
                    hasDefaultDataSets: true,
                },
            }))
        },
        setIsChartSummaryHovered: (value: boolean) => {
            set((state) => ({
                usageAndCost: {
                    ...state.usageAndCost,
                    isChartSummaryHovered: value,
                },
            }))
        },
        setIsReportSettingsOpen: (value: boolean) => {
            set((state) => ({
                usageAndCost: {
                    ...state.usageAndCost,
                    isReportSettingsOpen: value,
                },
            }))
        },
    },
    benchmarking: {
        reportSettings: DEFAULT_BENCHMARKING_REPORT_SETTINGS,
        hasDefaultSettings: true,
        hoveredDisplaySetting: null,
        selectedView: 'chart',
        setReportSettings: (
            partialSettings: Partial<BenchmarkingReportSettings>
        ) => {
            set((state) => {
                const updatedSettings: BenchmarkingReportSettings = {
                    ...state.benchmarking.reportSettings,
                    ...partialSettings,
                }

                return {
                    benchmarking: {
                        ...state.benchmarking,
                        reportSettings: updatedSettings,
                        hasDefaultSettings:
                            checkDefaultSettings(updatedSettings),
                    },
                }
            })
        },

        resetReportSettings: () => {
            set((state) => ({
                benchmarking: {
                    ...state.benchmarking,
                    reportSettings: DEFAULT_BENCHMARKING_REPORT_SETTINGS,
                    hasDefaultSettings: true,
                },
            }))
        },

        setHoveredDisplaySetting: (entity) => {
            set((state) => ({
                benchmarking: {
                    ...state.benchmarking,
                    hoveredDisplaySetting: entity,
                },
            }))
        },

        setSelectedView: (view) => {
            set((state) => ({
                benchmarking: {
                    ...state.benchmarking,
                    selectedView: view,
                },
            }))
        },
    },
}))

export default useAnalyticsStore
